import {
  endOfDay,
  endOfMonth,
  endOfYear,
  format,
  startOfDay,
  startOfMonth,
  startOfYear,
  subMonths,
  subYears,
} from 'date-fns';
import { isMoment } from 'moment/moment';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { BehaviorSubject } from 'rxjs';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Component, DestroyRef, EventEmitter, OnInit, Output } from '@angular/core';
import { GalaxyFormFieldModule } from '@vendasta/galaxy/form-field';
import { MatSelectModule } from '@angular/material/select';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { CommonModule } from '@angular/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslationModule } from '../../../i18n';
import { DateRange } from '../interfaces';
import { provideNativeDateAdapter } from '@angular/material/core';

export interface TimeRangeOption {
  dateRange?: DateRange;
  name: string;
  id: string;
}

@Component({
  selector: 'crm-date-range-selector',
  templateUrl: './date-range-selector.component.html',
  styleUrl: './date-range-selector.component.scss',
  imports: [
    CommonModule,
    GalaxyFormFieldModule,
    ReactiveFormsModule,
    TranslateModule,
    TranslationModule,
    MatSelectModule,
    MatDatepickerModule,
  ],
  providers: [provideNativeDateAdapter()],
})
export class DateRangeSelectorComponent implements OnInit {
  @Output() dateRangeChanged = new EventEmitter<DateRange>();

  currentDate = new Date();
  lastMonth = subMonths(this.currentDate, 1);
  lastYear = subYears(this.currentDate, 1);
  CUSTOM_DATE_ID = 'customDate';

  timeRangeOptions: TimeRangeOption[] = [
    {
      dateRange: {
        dateRangeStart: startOfMonth(this.currentDate),
        dateRangeEnd: endOfMonth(this.currentDate),
      },
      name: `${this.translate.instant('LEADERBOARD.DATE_RANGES.THIS_MONTH')} (${format(this.currentDate, 'MMMM yyyy')})`,
      id: 'thisMonth',
    },
    {
      dateRange: {
        dateRangeStart: startOfMonth(this.lastMonth),
        dateRangeEnd: endOfMonth(this.lastMonth),
      },
      name: `${this.translate.instant('LEADERBOARD.DATE_RANGES.LAST_MONTH')} (${format(this.lastMonth, 'MMMM yyyy')})`,
      id: 'lastMonth',
    },
    {
      dateRange: {
        dateRangeStart: startOfYear(this.currentDate),
        dateRangeEnd: endOfYear(this.currentDate),
      },
      name: `${this.translate.instant('LEADERBOARD.DATE_RANGES.THIS_YEAR')} (${format(this.currentDate, 'yyyy')})`,
      id: 'thisYear',
    },
    {
      dateRange: {
        dateRangeStart: startOfYear(this.lastYear),
        dateRangeEnd: endOfYear(this.lastYear),
      },
      name: `${this.translate.instant('LEADERBOARD.DATE_RANGES.LAST_YEAR')} (${format(this.lastYear, 'yyyy')})`,
      id: 'lastYear',
    },
    { name: `${this.translate.instant('LEADERBOARD.DATE_RANGES.CUSTOM')}`, id: this.CUSTOM_DATE_ID },
  ];

  protected customRangeStart = new FormControl<Date | null>(null);
  protected customRangeEnd = new FormControl<Date | null>(null);

  private readonly timeRange$$ = new BehaviorSubject<DateRange>(this.timeRangeOptions[0].dateRange);
  public readonly showCustomDateSelector$$ = new BehaviorSubject<boolean>(false);

  constructor(
    private readonly translate: TranslateService,
    private readonly destroyRef: DestroyRef,
  ) {}

  ngOnInit(): void {
    this.timeRange$$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((timeRange) => {
      if (timeRange?.dateRangeStart && timeRange?.dateRangeEnd) {
        this.dateRangeChanged.emit(timeRange);
      }
    });
  }

  setTimeRange(event: TimeRangeOption): void {
    if (event.id === this.CUSTOM_DATE_ID) {
      this.showCustomDateSelector$$.next(true);
      this.triggerCustomRangeChanged();
    } else {
      this.showCustomDateSelector$$.next(false);
      this.timeRange$$.next(event.dateRange);
    }
  }

  triggerCustomRangeChanged() {
    let start = this.customRangeStart.value;
    let end = this.customRangeEnd.value;
    start = isMoment(start) ? start.toDate() : start;
    end = isMoment(end) ? end.toDate() : end;

    if (start && end) {
      this.timeRange$$.next({
        dateRangeStart: startOfDay(start),
        dateRangeEnd: endOfDay(end),
      });
    }
  }
}
